import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { startOfTodayLocal } from "@/lib/celebrationDates";

/**
 * MS263: prefetch the heaviest dashboard tab queries when the
 * Dashboard mounts so subsequent tab navigation is instant.
 *
 * Lucky's report (2026-05-18): "things take a while to load up...
 * if I want to click on other tabs inside that dashboard, it takes
 * a while to load them up, at least 3-4 seconds, maybe more... we
 * should load up everything so that, when the user gets on to the
 * dashboard, which might take a little longer than loading up a
 * landing page, at least everything loads very quickly."
 *
 * Right approach: each tab page uses `useQuery` with a unique
 * queryKey. By calling `queryClient.prefetchQuery` with the SAME
 * queryKey + queryFn from the Dashboard mount, the cache warms
 * up in the background. When the user navigates to /team, /orders,
 * /calendar, /wishlist, the useQuery hook resolves synchronously
 * from cache instead of issuing a network round-trip.
 *
 * Prefetch fires fire-and-forget. Failures are silent (the per-page
 * useQuery will retry on its own). We deliberately don't await any
 * of them — Dashboard renders immediately, and the prefetch races
 * in parallel with the user's first paint.
 *
 * Caveats:
 * - Queries must match queryKey EXACTLY (including options like
 *   companyId being a string vs. null). Mismatches mean two cache
 *   entries and the prefetch is dead weight.
 * - Tabs whose pages already declare their useQuery with the same
 *   key benefit instantly. Tabs whose pages do additional per-row
 *   enrichment (e.g. Orders → image_url batch fetch) still pay that
 *   cost on first render — fix is to lift enrichment into the
 *   prefetch path too, but staleTime keeps it warm for subsequent
 *   navigations.
 */
export function usePrefetchDashboardTabs(
  companyId: string | null | undefined,
  userId: string | null | undefined,
): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!companyId || !userId) return;

    // Team — full members list with all the fields the /team page reads.
    void queryClient.prefetchQuery({
      queryKey: ["team_members", companyId, "all"],
      queryFn: async () => {
        const { data, error } = await supabase
          .from("team_members")
          .select("*")
          .eq("company_id", companyId)
          .order("name")
          .limit(500); // MS397: mirrors useTeamMembers safety ceiling
        if (error) throw error;
        return data ?? [];
      },
      staleTime: 30_000,
    });

    // Orders — list page. Skip the image_url enrichment in the
    // prefetch path (the page will run that on first render); the
    // basic row data is what makes the table pop in instantly.
    void queryClient.prefetchQuery({
      queryKey: ["orders", companyId, "list"],
      queryFn: async () => {
        const { data, error } = await supabase
          .from("orders")
          .select("id, reference, team_member_name, celebration_type, delivery_date, status, payment_status, budget_cents, created_at, auto_approved, cake_type, cake_size, vendor_category")
          .eq("company_id", companyId)
          .order("created_at", { ascending: false })
          .limit(100); // MS397: mirrors useOrdersList safety ceiling
        if (error) throw error;
        return data ?? [];
      },
      staleTime: 30_000,
    });

    // Wishlist — intentionally NOT prefetched here.
    // MS445: the previous prefetch used a PostgREST !inner join
    // (vendor_products!inner), returning rows shaped as:
    //   { id, sku_id, created_at, vendor_products: { ... } }
    // useWishlist assembles its WishlistRow as:
    //   { id, sku_id, created_at, sku: { ... } | null }
    // These shapes don't match, so during the 30-second stale window
    // every saved gift rendered as "not available" (w.sku === undefined)
    // before the real queryFn ran and fixed it.
    //
    // Duplicating useWishlist's two-query + sku-map assembly here to
    // produce the correct shape would be fragile; the wishlist is a
    // secondary surface whose cold-start (two small queries) is fast
    // enough that prefetching isn't worth the maintenance burden.
    // Remove if this comment ever becomes stale because the Wishlist
    // page's stale-start becomes a UX complaint.

    // Notifications — small payload, fast.
    // MS444: two bugs here meant the prefetch was completely inert:
    //   1. queryKey was ["notifications", userId] but useNotifications uses
    //      ["notifications", userId, "all"] → different cache entries, cache miss
    //      every time the Notifications page or unread-badge consumer loaded.
    //   2. SELECT had "read" (non-existent column) instead of "is_read", so even
    //      if the key had matched, the cached rows would have had is_read=undefined.
    // Now matches useNotifications exactly (key + column list). The .limit(50)
    // vs useNotifications's .limit(200) is intentional: the prefetch warms a
    // fast first-paint, then stale-while-revalidate loads the full 200 in the
    // background once the user reaches /notifications.
    void queryClient.prefetchQuery({
      queryKey: ["notifications", userId, "all"],
      queryFn: async () => {
        const { data, error } = await supabase
          .from("notifications")
          .select("id, title, message, type, is_read, created_at, related_id, related_type")
          .eq("user_id", userId)
          .order("created_at", { ascending: false })
          .limit(50);
        if (error) throw error;
        return data ?? [];
      },
      staleTime: 15_000,
    });

    // Calendar — useAllCelebrations (key: ["celebrations", companyId, "all"]).
    // MS448: the previous comment here claimed Calendar got a free cache hit
    // from the Dashboard's useUpcomingCelebrations, but those use a DIFFERENT
    // key (["celebrations", companyId, "upcoming", from, to, limit]) so there
    // was never any overlap. Calendar's full list (sorted by date ascending,
    // used to render the celebration timeline + trigger the horizon upsert)
    // was never prefetched. Now it is.
    void queryClient.prefetchQuery({
      queryKey: ["celebrations", companyId, "all"],
      queryFn: async () => {
        const { data, error } = await supabase
          .from("celebrations")
          .select("id, company_id, team_member_id, type, date, status")
          .eq("company_id", companyId)
          .order("date", { ascending: true });
        if (error) throw error;
        return data ?? [];
      },
      staleTime: 30_000,
    });

    // Approved celebrations still waiting on an order (Orders action rail).
    const todayStr = format(startOfTodayLocal(), "yyyy-MM-dd");
    void queryClient.prefetchQuery({
      queryKey: ["celebrations", companyId, "approved-pending-order"],
      queryFn: async () => {
        const { data, error } = await supabase
          .from("celebrations")
          .select(
            `id, date, type, status, team_member_id,
             team_members(id, name, start_date, dietary_requirements),
             celebration_recommendations(id, line_items, total_cents, status, approved_at)`,
          )
          .eq("company_id", companyId)
          .eq("status", "approved")
          .gte("date", todayStr)
          .order("date", { ascending: true });
        if (error) throw error;
        return data ?? [];
      },
      staleTime: 30_000,
    });
  }, [companyId, userId, queryClient]);
}
